import { useRef, useState } from 'react';
import { Loader2, Play, X } from 'lucide-react';
import { triggerRebalance } from '../../api/sectorTaxonomyApi';
import { useAuthStore } from '../../stores/authStore';

interface Props {
  onClose: () => void;
  onTriggered: (runId: string) => void;
  onError: (msg: string) => void;
  /**
   * 외부에서 주입하는 "이미 진행 중" 신호. 부모 페이지가 useRealtimeJobs 의
   * activeJobs (LLM_GATE_E) 또는 runningRunId 를 종합해 true 를 내려주면
   * 실행 버튼이 disable 되고 라벨이 "리밸런싱 중..." 으로 바뀐다.
   * 권위: dashboard-api 의 GATE-E 이중 트리거 사고 (08:49 + 08:52) 회귀 방지.
   */
  inFlight?: boolean;
}

// Type-A 실행 확인 모달.
// 비용이 발생하는 액션이므로 2단계 확인 체크박스 + confirmToken 생성 + disable 로직.
// 권위: ARCH_DASHBOARD §4.11 / Proposal v0.3 §2.7, §6.
export function RebalanceTriggerModal({
  onClose,
  onTriggered,
  onError,
  inFlight = false,
}: Props): JSX.Element {
  const [understood, setUnderstood] = useState(false);
  const [acknowledge, setAcknowledge] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // setState 는 비동기/배치 처리되므로 같은 tick 안의 빠른 연속 클릭이 가드를
  // 모두 통과할 수 있다. ref 로 동기 lock 을 걸어 race 를 차단한다.
  const submittingRef = useRef(false);
  const user = useAuthStore((s) => s.user);
  const triggerBy = user?.email ?? 'unknown';
  const isBusy = submitting || inFlight;
  const canSubmit = understood && acknowledge && !isBusy;

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await triggerRebalance(triggerBy);
      onTriggered(res.runId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'rebalance_trigger_failed';
      onError(`Type-A 실행 실패: ${msg}`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div className="tx-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="rebal-title">
      <div className="tx-modal">
        <h3 id="rebal-title" className="tx-modal__title">
          전체 리밸런싱 실행 (Type-A)
        </h3>
        <div className="tx-modal__body">
          <p>
            Type-A 는 <strong>전체 원천 섹터를 LLM 이 재검토</strong> 하여 정규 섹터
            택소노미 전체를 재생성합니다. 기존 매핑은 유지되며, 제안된 변경
            사항은 <em>리뷰 큐 &gt; Type-A 대기열</em> 에서 운영자가 승인해야
            반영됩니다.
          </p>
          <ul>
            <li>
              예상 비용: <span className="tx-modal__cost">~$0.17 USD</span> (Proposal v0.3 §6)
            </li>
            <li>모델: claude-sonnet-4-5</li>
            <li>소요: 분 단위 비동기 배치</li>
            <li>
              소급 재계산 없음 — 다음 주도주 배치부터 매핑 반영
            </li>
          </ul>
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            data-testid="rebal-ack-1"
          />
          <span>비용 ~$0.17 가 발생함을 이해합니다.</span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={acknowledge}
            onChange={(e) => setAcknowledge(e.target.checked)}
            data-testid="rebal-ack-2"
          />
          <span>
            결과는 리뷰 큐를 통해 수동으로 확인·승인해야 반영됨을 이해합니다.
          </span>
        </label>

        <div className="pca-muted" style={{ fontSize: 11, marginTop: 4 }}>
          trigger_by: <code>{triggerBy}</code>
        </div>

        <div className="tx-modal__actions">
          <button type="button" className="pca-btn" onClick={onClose}>
            <X size={14} strokeWidth={1.75} />
            취소
          </button>
          <button
            type="button"
            className="pca-btn pca-btn--primary"
            disabled={!canSubmit}
            aria-busy={isBusy}
            title={
              inFlight
                ? '현재 GATE-E Type-A 진행 중 (예상 ~9분)'
                : '실행'
            }
            onClick={handleSubmit}
            data-testid="rebal-submit"
          >
            {isBusy ? (
              <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
            ) : (
              <Play size={14} strokeWidth={1.75} />
            )}
            {isBusy ? '리밸런싱 중...' : '실행'}
          </button>
        </div>
      </div>
    </div>
  );
}
